import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { ChevronDown, Plus, Search, Lock, EyeOff, Pin, PinOff, Settings2, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { barVars, tabWidgetCounts } from "@/lib/dashboardTabs";

// Item 97 §2/§9/§13/§14/§15/§21 — the dashboard tab bar.
// Positions: top | bottom | left | right · Styles: underline | pill | segmented | button
// Overflow: scroll | wrap | dropdown · Mobile: swipe-scroll | dropdown | accordion
// Keyboard: ←/→ (or ↑/↓ when vertical), Home/End, Enter/Space, Ctrl+PageUp/PageDown anywhere.

const PIN_KEY = (dashId) => `eip_tabpins_${dashId}`;
const readPins = (dashId) => { try { return JSON.parse(localStorage.getItem(PIN_KEY(dashId))) || []; } catch { return []; } };
const writePins = (dashId, list) => { try { localStorage.setItem(PIN_KEY(dashId), JSON.stringify(list)); } catch { /* noop */ } };

const toPascal = (s) => String(s || "").split(/[-_\s]/).filter(Boolean).map((p) => p[0].toUpperCase() + p.slice(1)).join("");

export function TabIcon({ tab, className = "w-3.5 h-3.5" }) {
  if (tab.icon_url) return <img src={tab.icon_url} alt="" className={cn(className, "object-contain")} />;
  if (!tab.icon) return null;
  const Cmp = Icons[toPascal(tab.icon)];
  return Cmp ? <Cmp className={className} aria-hidden="true" /> : null;
}

function Badge({ tab }) {
  const b = tab.badge || {};
  if (!b.kind || b.kind === "none") return null;
  const color = b.color || "var(--brand-coral)";
  if (b.kind === "dot") return <span data-testid={`tab-badge-${tab.id}`} className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: color }} />;
  return (
    <span data-testid={`tab-badge-${tab.id}`} className="shrink-0 text-[9px] leading-none px-1.5 py-0.5 rounded-full text-white"
      style={{ background: color }}>{b.value || (b.kind === "count" ? "0" : "")}</span>
  );
}

export function DashboardTabs({
  dashboardId, tabs, activeId, onSelect, bar, counts = null, editable = false, mobile = false,
  onAdd, onReorder, onManage, onContextMenu, hiddenNote = true,
}) {
  const vertical = bar.position === "left" || bar.position === "right";
  const [q, setQ] = useState("");
  const [pins, setPins] = useState(() => readPins(dashboardId));
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const listRef = useRef(null);
  const wcounts = useMemo(() => counts || tabWidgetCounts([], tabs), [counts, tabs]);

  const togglePin = (id) => {
    const next = pins.includes(id) ? pins.filter((x) => x !== id) : [...pins, id];
    setPins(next); writePins(dashboardId, next);
  };

  // pinned tabs first, then author order
  const ordered = useMemo(() => {
    const pinned = tabs.filter((t) => pins.includes(t.id));
    const rest = tabs.filter((t) => !pins.includes(t.id));
    return [...pinned, ...rest];
  }, [tabs, pins]);

  const searched = useMemo(() => {
    if (!q.trim()) return ordered;
    const needle = q.trim().toLowerCase();
    return ordered.filter((t) => t.name.toLowerCase().includes(needle));
  }, [ordered, q]);

  // Ctrl+PageUp / Ctrl+PageDown switch tabs from anywhere on the dashboard
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey) || !["PageUp", "PageDown"].includes(e.key)) return;
      const list = tabs.filter((t) => !t.disabled);
      const i = list.findIndex((t) => t.id === activeId);
      if (i < 0) return;
      e.preventDefault();
      const next = list[(i + (e.key === "PageDown" ? 1 : -1) + list.length) % list.length];
      if (next) onSelect(next.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [tabs, activeId, onSelect]);

  const onKeyDown = (e) => {
    const list = searched.filter((t) => !t.disabled);
    const i = list.findIndex((t) => t.id === activeId);
    const nextKey = vertical ? "ArrowDown" : "ArrowRight";
    const prevKey = vertical ? "ArrowUp" : "ArrowLeft";
    let target = null;
    if (e.key === nextKey) target = list[(i + 1) % list.length];
    else if (e.key === prevKey) target = list[(i - 1 + list.length) % list.length];
    else if (e.key === "Home") target = list[0];
    else if (e.key === "End") target = list[list.length - 1];
    else return;
    e.preventDefault();
    if (target) {
      onSelect(target.id);
      const el = listRef.current?.querySelector(`[data-tab-id="${target.id}"]`);
      el?.focus();
    }
  };

  const dropOn = (id) => {
    if (!editable || !dragId || dragId === id || !onReorder) return;
    const ids = tabs.map((t) => t.id).filter((x) => x !== dragId);
    const at = ids.indexOf(id);
    ids.splice(at < 0 ? ids.length : at, 0, dragId);
    onReorder(ids);
    setDragId(null); setOverId(null);
  };

  const styleFor = (t, isActive) => {
    const base = "relative flex items-center gap-1.5 whitespace-nowrap transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-ocean)]";
    const size = "px-3 py-1.5";
    const fonts = { fontSize: "var(--tb-font)", fontWeight: "var(--tb-weight)", fontFamily: "var(--tb-family)" };
    const st = bar.style;
    const common = {
      ...fonts,
      color: t.disabled ? "var(--tb-disabled)" : isActive ? "var(--tb-sel-text)" : "var(--tb-text)",
      fontStyle: bar.italic ? "italic" : undefined,
      textDecoration: bar.underline ? "underline" : undefined,
      opacity: t.disabled ? 0.55 : 1,
    };
    if (st === "pill") {
      return { cls: cn(base, size, "rounded-full"), style: { ...common, background: isActive ? "var(--tb-sel-bg)" : "transparent", borderRadius: 999 } };
    }
    if (st === "segmented") {
      return { cls: cn(base, size, "border-r last:border-r-0"), style: { ...common, background: isActive ? "var(--tb-sel-bg)" : "transparent", borderColor: "var(--tb-border)" } };
    }
    if (st === "button") {
      return {
        cls: cn(base, size, "border shadow-sm"),
        style: { ...common, background: isActive ? "var(--tb-sel-bg)" : "var(--surface)", borderColor: "var(--tb-border)", borderRadius: "var(--tb-radius)" },
      };
    }
    return { cls: cn(base, size, "rounded-t-lg"), style: { ...common, background: isActive ? "var(--tb-sel-bg)" : "transparent" } };
  };

  const Indicator = ({ show }) => {
    if (!show || bar.style !== "underline" || bar.indicator_position === "none") return null;
    const pos = bar.indicator_position || (vertical ? "left" : "bottom");
    const s = "var(--tb-ind-size)";
    const style = { background: "var(--tb-ind)", position: "absolute" };
    if (pos === "top") Object.assign(style, { top: 0, left: 6, right: 6, height: s });
    else if (pos === "left") Object.assign(style, { left: 0, top: 4, bottom: 4, width: s });
    else if (pos === "right") Object.assign(style, { right: 0, top: 4, bottom: 4, width: s });
    else Object.assign(style, { bottom: 0, left: 6, right: 6, height: s });
    return <span aria-hidden="true" style={style} className="rounded-full" />;
  };

  const TabButton = ({ t }) => {
    const isActive = t.id === activeId;
    const { cls, style } = styleFor(t, isActive);
    return (
      <button
        key={t.id}
        data-testid={`dashboard-tab-${t.id}`}
        data-tab-id={t.id}
        role="tab"
        aria-selected={isActive}
        aria-disabled={t.disabled || undefined}
        tabIndex={isActive ? 0 : -1}
        disabled={t.disabled}
        draggable={editable}
        onDragStart={() => editable && setDragId(t.id)}
        onDragOver={(e) => { if (editable && dragId) { e.preventDefault(); setOverId(t.id); } }}
        onDragLeave={() => setOverId((x) => (x === t.id ? null : x))}
        onDrop={(e) => { e.preventDefault(); dropOn(t.id); }}
        onDragEnd={() => { setDragId(null); setOverId(null); }}
        onContextMenu={editable && onContextMenu ? (e) => { e.preventDefault(); onContextMenu(t, e); } : undefined}
        onClick={() => !t.disabled && onSelect(t.id)}
        className={cn(cls, overId === t.id && "ring-2 ring-[color:var(--brand-ocean)] ring-offset-0", dragId === t.id && "opacity-50")}
        style={style}
        onMouseEnter={(e) => { if (!t.disabled && !isActive) { e.currentTarget.style.color = "var(--tb-hover-text)"; if (bar.style !== "underline") e.currentTarget.style.background = "var(--tb-hover-bg)"; } }}
        onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.color = t.disabled ? "var(--tb-disabled)" : "var(--tb-text)"; if (bar.style !== "underline") e.currentTarget.style.background = bar.style === "button" ? "var(--surface)" : "transparent"; } }}
        title={t.locked ? `${t.name} (locked)` : t.name}
      >
        <TabIcon tab={t} />
        <span className="truncate max-w-[180px]">{t.name}</span>
        <Badge tab={t} />
        {editable && hiddenNote && t.hidden && <EyeOff data-testid={`tab-hidden-${t.id}`} className="w-3 h-3 opacity-60" />}
        {editable && t.locked && <Lock className="w-3 h-3 opacity-60" />}
        <Indicator show={isActive} />
      </button>
    );
  };

  // ---- mobile presentations ------------------------------------------------
  if (mobile && bar.mobile !== "scroll") {
    const active = tabs.find((t) => t.id === activeId) || tabs[0] || {};
    if (bar.mobile === "dropdown") {
      return (
        <div data-testid="dashboard-tabs-mobile" className="px-3 py-2 border-b border-[color:var(--glass-border)]" style={barVars(bar)}>
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="dashboard-tabs-mobile-trigger" className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-[color:var(--surface-2)] text-sm text-[color:var(--ink)]">
                <span className="flex items-center gap-2 truncate"><TabIcon tab={active} />{active.name}</span>
                <ChevronDown className="w-4 h-4 text-[color:var(--ink-3)]" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-[260px] p-1">
              {tabs.map((t) => (
                <button key={t.id} data-testid={`dashboard-tab-${t.id}`} disabled={t.disabled} onClick={() => onSelect(t.id)}
                  className={cn("w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm",
                    t.id === activeId ? "bg-[color:var(--surface-2)] text-[color:var(--ink)]" : "text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]")}>
                  <TabIcon tab={t} /><span className="truncate flex-1">{t.name}</span><Badge tab={t} />
                </button>
              ))}
            </PopoverContent>
          </Popover>
        </div>
      );
    }
    return (
      <div data-testid="dashboard-tabs-mobile" className="border-b border-[color:var(--glass-border)] divide-y divide-[color:var(--glass-border)]" style={barVars(bar)}>
        {tabs.map((t) => (
          <button key={t.id} data-testid={`dashboard-tab-${t.id}`} disabled={t.disabled} onClick={() => onSelect(t.id)}
            className={cn("w-full text-left flex items-center gap-2 px-3 py-2.5 text-sm",
              t.id === activeId ? "bg-[color:var(--surface-2)] text-[color:var(--ink)] font-medium" : "text-[color:var(--ink-2)]")}>
            <TabIcon tab={t} /><span className="truncate flex-1">{t.name}</span><Badge tab={t} />
          </button>
        ))}
      </div>
    );
  }

  const overflowDropdown = bar.overflow === "dropdown";
  const wrap = bar.overflow === "wrap";
  const visibleTabs = overflowDropdown ? searched.slice(0, vertical ? 99 : 6) : searched;
  const hiddenTabs = overflowDropdown ? searched.slice(visibleTabs.length) : [];

  return (
    <div
      data-testid="dashboard-tabs"
      role="tablist"
      aria-label="Dashboard tabs"
      aria-orientation={vertical ? "vertical" : "horizontal"}
      onKeyDown={onKeyDown}
      style={{
        ...barVars(bar),
        background: "var(--tb-bg)",
        padding: "var(--tb-pad)",
        ...(vertical ? { width: "var(--tb-width)" } : { minHeight: "var(--tb-height)" }),
        ...(bar.shadow ? { boxShadow: "var(--shadow-md)" } : {}),
      }}
      className={cn(
        "shrink-0 flex",
        vertical ? "flex-col gap-[var(--tb-gap)] overflow-y-auto" : "items-center gap-[var(--tb-gap)]",
        !vertical && (wrap ? "flex-wrap" : "overflow-x-auto"),
        bar.divider && (bar.position === "bottom" ? "border-t" : bar.position === "left" ? "border-r" : bar.position === "right" ? "border-l" : "border-b"),
        bar.divider && "border-[color:var(--tb-border)]",
      )}
    >
      <div ref={listRef} className={cn("flex", vertical ? "flex-col gap-[var(--tb-gap)]" : "items-center gap-[var(--tb-gap)]",
        !vertical && wrap && "flex-wrap",
        bar.style === "segmented" && !vertical && "rounded-[var(--tb-radius)] border border-[color:var(--tb-border)] overflow-hidden",
        bar.align === "center" && !vertical && "mx-auto", bar.align === "end" && !vertical && "ml-auto")}>
        {visibleTabs.map((t) => <TabButton key={t.id} t={t} />)}
      </div>

      {hiddenTabs.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <button data-testid="dashboard-tabs-overflow" className="flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] text-[color:var(--ink-3)] hover:bg-[color:var(--surface-2)]">
              +{hiddenTabs.length}<ChevronDown className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[240px] p-1">
            {hiddenTabs.map((t) => (
              <button key={t.id} data-testid={`dashboard-tab-${t.id}`} disabled={t.disabled} onClick={() => onSelect(t.id)}
                className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]">
                <TabIcon tab={t} /><span className="truncate flex-1">{t.name}</span>
              </button>
            ))}
          </PopoverContent>
        </Popover>
      )}

      <div className={cn("flex items-center gap-0.5", vertical ? "mt-1" : "ml-auto")}>
        {bar.searchable && tabs.length > 4 && (
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="dashboard-tabs-search-btn" title="Search tabs" className="p-1.5 rounded-lg text-[color:var(--ink-3)] hover:bg-[color:var(--surface-2)]">
                <Search className="w-3.5 h-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-[260px] p-2 space-y-1">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]" />
                <Input data-testid="dashboard-tabs-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search tabs…"
                  className="h-8 pl-7 text-xs bg-[color:var(--surface-2)]" />
                {q && <button onClick={() => setQ("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--ink-3)]"><X className="w-3 h-3" /></button>}
              </div>
              <div className="max-h-[220px] overflow-y-auto">
                {searched.map((t) => (
                  <div key={t.id} className="flex items-center gap-1">
                    <button data-testid={`tab-search-result-${t.id}`} onClick={() => onSelect(t.id)}
                      className="flex-1 text-left flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-[color:var(--ink-2)] hover:bg-[color:var(--surface-2)]">
                      <TabIcon tab={t} /><span className="truncate flex-1">{t.name}</span>
                      <span className="text-[10px] text-[color:var(--ink-3)]">{wcounts[t.id] ?? 0}</span>
                    </button>
                    <button data-testid={`tab-pin-${t.id}`} title={pins.includes(t.id) ? "Unpin" : "Pin to front"} onClick={() => togglePin(t.id)}
                      className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]">
                      {pins.includes(t.id) ? <PinOff className="w-3 h-3" /> : <Pin className="w-3 h-3" />}
                    </button>
                  </div>
                ))}
                {!searched.length && <p className="px-2 py-2 text-[11px] text-[color:var(--ink-3)]">No tabs match “{q}”.</p>}
              </div>
            </PopoverContent>
          </Popover>
        )}
        {editable && onAdd && (
          <button data-testid="dashboard-tab-add" title="Add tab" onClick={onAdd}
            className="p-1.5 rounded-lg text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]">
            <Plus className="w-4 h-4" />
          </button>
        )}
        {editable && onManage && (
          <button data-testid="dashboard-tabs-manage" title="Manage tabs" onClick={onManage}
            className="p-1.5 rounded-lg text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)] hover:bg-[color:var(--surface-2)]">
            <Settings2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
}
